import {
  Autosuggest,
  AutosuggestProps,
  Box,
  Button,
  ColumnLayout,
  FormField,
  Input,
  Link,
  TextContent,
} from '@amzn/awsui-components-core';
import constants from '../../common/constants';
import { getContentPath } from '../../common/ContentPaths';
import React, { useContext, useEffect, useState } from 'react';
import CollectionStoryWithIndex from './CollectionStoryWithIndex';
import {
  CollectionStory,
  ContentType,
  StoryAttachment,
  StoryRepo,
} from '@amzn/awscat-pattern-library-service-model';
import { decodeSearchtextString } from '../utils/Search';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import CollectionEpicWrapper from './CollectionEpicWrapper';
import { NUMBER_OF_PUBLICATION_SEARCH_RESULTS } from '../../api/UtilsApi';
import { AppContext } from '../../contexts/AppContext';
import { convertEntities } from '@amzn/awscat-editor';

interface StoryPublicationProps {
  item: CollectionStoryWithIndex;
  epicNumber: number;
  epics: CollectionEpicWrapper[];
  setEpics: (epics: CollectionEpicWrapper[]) => void;
}

function StoryPublication({ item, epicNumber, epics, setEpics }: StoryPublicationProps) {
  const appContext = useContext(AppContext);
  const { entities } = appContext;
  const mplService: IMplServiceClient = MplServiceClientFactory.create();

  // For finding publication and auto-populating the attachment & repo
  const [getPublication, setGetPublication] = useState<boolean>(false);
  const [publicationId, setPublicationId] = useState<string>('');
  const [epicIndex, setEpicIndex] = useState<number>(0);
  const [storyWithIndex, setStoryWithIndex] = useState<CollectionStoryWithIndex>();

  const [statusType, setStatusType] = useState<AutosuggestProps.StatusType>('pending');

  // For autosuggest search for publications
  const [gettingSuggestions, setGettingSuggestions] = useState<boolean>(false);
  const [suggestions, setSuggestions] =
    useState<Array<AutosuggestProps.Option | AutosuggestProps.OptionGroup>>();
  const validUrlPrefix = window.location.origin + constants.EDIT_COLLECTION_GUIDANCE_URL_PREFIX;

  useEffect(() => {
    const getPublicationInfo = async () => {
      const publication = await mplService.getPublication(publicationId);
      const epicsCopy = [...epics];
      if (storyWithIndex) {
        const storyAttachment = publication.attachments?.map((attachment) =>
          StoryAttachment.create(attachment.url, attachment.filename, attachment.attachmentId),
        );
        const storyRepos = publication.content.contentTypeMetadata.codeLinks.map((code) =>
          StoryRepo.create(code.url, code.label),
        );
        const updatedStory = CollectionStory.create(
          // @ts-expect-error TS2345 - strict migration
          storyWithIndex.story.title,
          storyWithIndex.story.description,
          publicationId,
          publication.content.contentMetadata.contentName,
          publication.content.contentMetadata.contentType,
          storyAttachment,
          storyRepos,
        );
        // @ts-expect-error TS2532 - strict migration
        epicsCopy[epicIndex].stories[storyWithIndex.index].story = updatedStory;
        // @ts-expect-error TS2532 - strict migration
        epicsCopy[epicIndex].stories[storyWithIndex.index].addedPublication = true;

        setEpics(epicsCopy);
      }
    };

    if (getPublication) {
      getPublicationInfo();
      setGetPublication(false);
    }
  }, [getPublication]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const getSuggestions = async () => {
      const epicCopy = [...epics];
      if (storyWithIndex) {
        // TODO: will be able to search by entity values in titles once updated cloudsearch index output
        const { publications, totalFound } = await mplService.searchPublications(
          // @ts-expect-error TS2532 - strict migration
          epicCopy[epicIndex].stories[storyWithIndex.index].searchTerm ?? '',
        );
        if (Array.isArray(publications) && publications.length > 0) {
          const results = publications.slice(0, NUMBER_OF_PUBLICATION_SEARCH_RESULTS);
          const options = results.map((publication) => {
            const guidanceType = 'Guidance type: ' + publication.contentType;
            const technology = 'Technology: ' + publication.mainTechnology;
            const option = {
              value: convertEntities(publication.contentName, entities),
              tags: [technology, guidanceType],
              filteringTags: [publication.contentId],
            };
            return option;
          });
          setSuggestions(options);
        } else {
          setSuggestions([]);
        }
      }
    };

    if (gettingSuggestions) {
      getSuggestions();
      setStatusType('finished');
    }
    setGettingSuggestions(false);
  }, [gettingSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeletePublication = () => {
    const epicCopy = [...epics];
    const updatedStory = CollectionStory.create(
      // @ts-expect-error TS2345 - strict migration
      item.story.title,
      item.story.description,
      '',
      '',
      ContentType.Pattern,
      [],
      [],
    );
    // @ts-expect-error TS2532 - strict migration
    epicCopy[epicNumber].stories[item.index].story = updatedStory;
    // @ts-expect-error TS2532 - strict migration
    epicCopy[epicNumber].stories[item.index].searchTerm = '';
    // @ts-expect-error TS2532 - strict migration
    epicCopy[epicNumber].stories[item.index].url = '';
    // @ts-expect-error TS2532 - strict migration
    epicCopy[epicNumber].stories[item.index].addedPublication = false;
    setGetPublication(false);
    setPublicationId('');
    setEpics(epicCopy);
    setEpicIndex(0);
    setStoryWithIndex(undefined);

    setSuggestions([]);
    setGettingSuggestions(false);
    setStatusType('pending');
  };

  const renderSearchPublicationAdded = () => {
    return (
      <ColumnLayout columns={2}>
        <div>
          <Box color="text-body-secondary">{item.story.contentType}</Box>
          <TextContent>
            <ul>
              <li>
                <Link external href={item.url}>
                  {convertEntities(item.story.publicationName ?? '', entities)}
                </Link>
              </li>
            </ul>
          </TextContent>
          <Button onClick={() => handleDeletePublication()}>Remove</Button>
        </div>
        {renderAttachmentsRepos(item.story.repos, item.story.attachments, item.index)}
      </ColumnLayout>
    );
  };

  const renderAttachmentsRepos = (
    repos?: StoryRepo[],
    attach?: StoryAttachment[],
    key?: number,
  ) => {
    const repositories =
      repos && repos.length > 0 ? (
        <div>
          <Box color="text-body-secondary">Repositories</Box>
          <TextContent>
            <ul>
              {repos.map((repo) => (
                <li key={key}>
                  <Link external href={repo.link}>
                    {repo.name}
                  </Link>
                </li>
              ))}
            </ul>
          </TextContent>
        </div>
      ) : null;

    const attachments =
      attach && attach.length > 0 ? (
        <div>
          <Box color="text-body-secondary">Attachments</Box>
          <TextContent>
            <ul>
              {attach.map((attachment) => (
                <li key={key}>
                  <Link external href={attachment.link}>
                    {decodeURIComponent(attachment.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </TextContent>
        </div>
      ) : null;

    return (
      <div>
        {repositories}
        {attachments}
      </div>
    );
  };

  const handleSearchPublicationURL = (url: string) => {
    const epicsCopy = [...epics];
    // @ts-expect-error TS2532 - strict migration
    epicsCopy[epicNumber].stories[item.index].url = url;
    setEpics(epicsCopy);

    if (url.includes(validUrlPrefix)) {
      // query for publication
      const publicationIdUrl = url.split('/').pop();
      if (publicationIdUrl && publicationIdUrl.length === 36) {
        setEpicIndex(epicNumber);
        setPublicationId(publicationIdUrl);
        setStoryWithIndex(item);
        setGetPublication(true);
      }
    }
  };

  const handleInputChange = (event: any) => {
    const epicCopy = [...epics];
    // @ts-expect-error TS2532 - strict migration
    epicCopy[epicNumber].stories[item.index].searchTerm = decodeSearchtextString(
      event.detail.value,
    );
    setEpicIndex(epicNumber);
    setStoryWithIndex(item);
    setEpics(epicCopy);
    setStatusType('loading');
    setGettingSuggestions(true);
  };

  const handleSelectPubliction = (event: any) => {
    const selectedOption = event.detail.selectedOption;

    if (selectedOption) {
      const urlPrefix = window.location.href.split(window.location.pathname);
      const contentId = selectedOption.filteringTags[0];
      const url = urlPrefix[0] + getContentPath({ contentId, mode: 'publication' });
      const epicsCopy = [...epics];
      // @ts-expect-error TS2532 - strict migration
      if (epicsCopy[epicNumber].stories) {
        // @ts-expect-error TS2532 - strict migration
        epicsCopy[epicNumber].stories[item.index].url = url;
      } else {
        // @ts-expect-error TS2532 - strict migration
        epicsCopy[epicNumber].stories = [
          new CollectionStoryWithIndex(
            CollectionStory.create('', '', '', '', ContentType.Pattern),
            0,
            url,
          ),
        ];
      }
      setEpics(epicsCopy);
      setEpicIndex(epicNumber);
      setPublicationId(contentId);
      setStoryWithIndex(item);
      setGetPublication(true);
    }
  };

  const renderSearchPublicationEdit = () => {
    return (
      <Box padding={'xxxs'}>
        <FormField
          label={constants.EDIT_COLLECTION_SEARCH_GUIDANCE_LABEL}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Autosuggest
            expandToViewport
            value={item.searchTerm ?? ''}
            placeholder={constants.EDIT_COLLECTION_SEARCH_GUIDANCE_PLACEHOLDER}
            loadingText={constants.LOADING_AUTO_SUGGESTIONS}
            errorText="Error fetching results."
            empty="No guidance found"
            statusType={statusType}
            options={item.searchTerm ? suggestions : []}
            filteringType="manual"
            enteredTextLabel={(text) => `Search term: "${text}"`}
            onChange={(event) => handleInputChange(event)}
            onSelect={(event) => handleSelectPubliction(event)}
            ariaRequired
          />
        </FormField>
        <FormField
          label={constants.EDIT_COLLECTION_GUIDANCE_URL_LABEL}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Input
            placeholder={validUrlPrefix}
            onChange={(event) => handleSearchPublicationURL(event.detail.value)}
            value={item.url ?? ''}
            ariaRequired
          />
        </FormField>
      </Box>
    );
  };

  return item.addedPublication || item?.story?.publicationId
    ? renderSearchPublicationAdded()
    : renderSearchPublicationEdit();
}

export default StoryPublication;
